import { Check, Heart, Languages, Target } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { btnGhost, btnPrimary, textLink } from '../components/ui/redesign';

const TYPES = [
  'INDIVIDUAL',
  'ANGEL',
  'ADVISER',
  'VC',
  'FAMILY_OFFICE',
  'STRATEGIC',
  'OTHER',
] as const;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const input =
  'min-h-[44px] w-full rounded-[12px] border-[1.5px] border-line bg-white px-3 py-[11px] text-[15px] outline-none focus:border-primary focus:shadow-[0_0_0_3px_rgba(58,125,68,0.12)]';
const labelCls = 'font-ui text-[13px] font-semibold';
const card = 'rounded-[18px] border border-line bg-white px-[22px] py-5';

function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const header = document.querySelector('header')?.getBoundingClientRect().height ?? 0;
  window.scrollTo({
    top: el.getBoundingClientRect().top + window.scrollY - header - 12,
    behavior: 'smooth',
  });
}

function Sent() {
  const { t } = useTranslation('investors');
  return (
    <div
      className="mt-[18px] flex items-center gap-2.5 rounded-[14px] bg-primary-pale p-4 font-medium text-primary-dark"
      role="status"
    >
      <Check className="h-5 w-5 flex-none" aria-hidden="true" />
      {t('sent')}
    </div>
  );
}

/** One of the two forms (PRD §7.2): materials request or a message to the founders. */
function InquiryForm({ kind }: { kind: 'MATERIALS' | 'CONTACT' }) {
  const { t, i18n } = useTranslation('investors');
  const [values, setValues] = useState({
    name: '',
    email: '',
    organization: '',
    investorType: '',
    message: '',
    website: '',
  });
  const [state, setState] = useState<'idle' | 'busy' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);
  const materials = kind === 'MATERIALS';
  const set = (key: keyof typeof values) => (e: { target: { value: string } }) =>
    setValues((v) => ({ ...v, [key]: e.target.value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!values.name.trim() || !values.email.trim()) return setError(t('errors.required'));
    if (!EMAIL.test(values.email.trim())) return setError(t('errors.email'));
    setState('busy');
    try {
      const api = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
      const res = await fetch(`${api}/investor-inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, ...values, locale: i18n.language }),
      });
      if (res.status === 429) throw new Error(t('errors.tooMany'));
      if (!res.ok) throw new Error(t('errors.failed'));
      setState('sent');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.failed'));
      setState('idle');
    }
  };

  if (state === 'sent') return <Sent />;
  const prefix = materials ? 'i' : 'c';
  return (
    <form className="mt-[18px]" noValidate onSubmit={submit}>
      <div className="grid grid-cols-1 gap-3.5 desk:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor={`${prefix}-name`} className={labelCls}>
            {t('form.name')}
          </label>
          <input
            id={`${prefix}-name`}
            required
            autoComplete="name"
            className={input}
            value={values.name}
            onChange={set('name')}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor={`${prefix}-email`} className={labelCls}>
            {t('form.email')}
          </label>
          <input
            id={`${prefix}-email`}
            type="email"
            required
            autoComplete="email"
            className={input}
            value={values.email}
            onChange={set('email')}
          />
        </div>
        {materials && (
          <>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="i-org" className={labelCls}>
                {t('form.firm')}
              </label>
              <input
                id="i-org"
                autoComplete="organization"
                className={input}
                value={values.organization}
                onChange={set('organization')}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="i-type" className={labelCls}>
                {t('form.type')}
              </label>
              <select
                id="i-type"
                className={input}
                value={values.investorType}
                onChange={set('investorType')}
              >
                <option value="">{t('form.select')}</option>
                {TYPES.map((v) => (
                  <option key={v} value={v}>
                    {t(`form.types.${v}`)}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
        <div className="flex flex-col gap-1.5 desk:col-span-2">
          <label htmlFor={`${prefix}-msg`} className={labelCls}>
            {t('form.message')}
          </label>
          <textarea
            id={`${prefix}-msg`}
            className={`${input} min-h-[110px] resize-y leading-[1.6]`}
            value={values.message}
            onChange={set('message')}
          />
        </div>
        {/* Honeypot: hidden from people, filled by bots (PRD §7.2) */}
        <input
          tabIndex={-1}
          aria-hidden="true"
          autoComplete="off"
          name="website"
          value={values.website}
          onChange={set('website')}
          className="absolute -left-[9999px] h-px w-px overflow-hidden"
        />
      </div>
      {error && <p className="mt-3 text-[14px] text-red-700">{error}</p>}
      <div className="mt-4 flex flex-wrap items-center gap-3.5">
        <button type="submit" className={btnPrimary} disabled={state === 'busy'}>
          {materials ? t('form.submit') : t('contact.submit')}
        </button>
        <span className="text-[12.5px] text-mute">
          {materials ? t('form.privacy') : t('contact.privacy')}
        </span>
      </div>
    </form>
  );
}

function Bullet({ children }: { children: ReactNode }) {
  return (
    <li className="flex gap-2 py-[7px] text-[14px] text-ink-2">
      <Check className="mt-[3px] h-4 w-4 flex-none text-primary" aria-hidden="true" />
      {children}
    </li>
  );
}

/** /investors (PRD §7, prototype `renderInvestors`): copy from the reference file, restyled. */
export default function Investors() {
  const { t } = useTranslation('investors');
  const build = [
    { Icon: Target, title: t('build.b1t'), body: t('build.b1p') },
    { Icon: Heart, title: t('build.b2t'), body: t('build.b2p') },
    { Icon: Languages, title: t('build.b3t'), body: t('build.b3p') },
  ];
  const pathTag =
    'mb-3 self-start rounded-full px-[11px] py-1 font-ui text-[12px] font-semibold tracking-[0.06em]';
  return (
    <div className="bg-warm-white font-text text-[15px] leading-[1.6] text-ink">
      <Helmet>
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
      </Helmet>
      <section className="mx-auto max-w-[1160px] px-4 desk:px-7">
        <div className="max-w-[46em] pb-2.5 pt-[54px]">
          <div className="font-ui text-[12.5px] font-semibold uppercase tracking-[0.08em] text-primary">
            {t('hero.eyebrow')}
          </div>
          <h1 className="mt-3 font-ui text-[clamp(30px,4.2vw,48px)] font-bold leading-[1.22] text-ink [text-wrap:balance]">
            {t('hero.titleLead')}
            <em className="not-italic text-primary">{t('hero.titleEm')}</em>
          </h1>
          <p className="mb-[22px] mt-3.5 max-w-[32em] text-[15px] text-ink-2 desk:text-[16.5px]">
            {t('hero.lede')}
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-3.5 desk:grid-cols-3">
          {(['1', '2', '3'] as const).map((n) => (
            <div key={n} className={card}>
              <b className="block font-ui text-[clamp(22px,2.6vw,30px)] font-bold leading-[1.15] text-primary-dark">
                {t(`stats.s${n}n`)}
              </b>
              <span className="mt-1 block text-[14px] text-ink-2">{t(`stats.s${n}l`)}</span>
            </div>
          ))}
        </div>

        <div className="mt-[50px]">
          <h2 className="font-ui text-[clamp(22px,2.6vw,30px)] font-bold leading-[1.6] text-ink">
            {t('build.title')}
          </h2>
          <p className="mt-2 max-w-[40em] text-ink-2">{t('build.intro')}</p>
          <div className="mt-5 grid grid-cols-1 gap-3.5 desk:grid-cols-3">
            {build.map(({ Icon, title, body }) => (
              <div key={title} className={card}>
                <h3 className="flex items-center gap-[9px] font-ui text-base font-semibold leading-[1.6] text-ink">
                  <i className="grid h-[30px] w-[30px] flex-none place-items-center rounded-full bg-primary-pale not-italic text-primary-dark">
                    <Icon className="h-4 w-4" aria-hidden="true" />
                  </i>
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-ink-2">{body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-[50px]">
          <h2 className="font-ui text-[clamp(22px,2.6vw,30px)] font-bold leading-[1.6] text-ink">
            {t('paths.title')}
          </h2>
          <div className="mt-5 grid grid-cols-1 items-stretch gap-4 desk:grid-cols-2">
            <div className="flex flex-col rounded-[20px] border border-line bg-white p-6">
              <span className={`${pathTag} bg-primary-pale text-primary-dark`}>
                {t('paths.giftTag')}
              </span>
              <h3 className="font-ui text-[20px] font-bold leading-[1.6]">
                {t('paths.giftTitle')}
              </h3>
              <div className="mt-1.5 font-bold text-primary-dark">{t('paths.giftAmount')}</div>
              <p className="mt-2 text-[14.5px] text-ink-2">{t('paths.giftBody')}</p>
              <ul className="mt-3">
                <Bullet>{t('paths.giftL1')}</Bullet>
                <Bullet>{t('paths.giftL2')}</Bullet>
              </ul>
              <div className="mt-auto flex flex-col gap-2.5 pt-[18px]">
                <Link to="/gift-cards" className={btnGhost}>
                  {t('paths.giftCta')}
                </Link>
              </div>
              <p className="mt-2.5 text-[12.5px] text-mute">{t('paths.giftNote')}</p>
            </div>
            <div className="order-first flex flex-col rounded-[20px] border-2 border-primary bg-white p-6 shadow-[0_20px_44px_-30px_rgba(58,125,68,0.75)] desk:order-none">
              <span className={`${pathTag} bg-primary text-white`}>{t('paths.investTag')}</span>
              <h3 className="font-ui text-[20px] font-bold leading-[1.6]">
                {t('paths.investTitle')}
              </h3>
              <div className="mt-1.5 font-bold text-primary-dark">{t('paths.investAmount')}</div>
              <p className="mt-2 text-[14.5px] text-ink-2">{t('paths.investBody')}</p>
              <ul className="mt-3">
                <Bullet>{t('paths.investL1')}</Bullet>
                <Bullet>{t('paths.investL2')}</Bullet>
                <Bullet>{t('paths.investL3')}</Bullet>
              </ul>
              <div className="mt-auto flex flex-col gap-2.5 pt-[18px]">
                <button
                  type="button"
                  className={btnPrimary}
                  onClick={() => scrollToId('materials-form')}
                >
                  {t('paths.investCta')}
                </button>
                <button
                  type="button"
                  className={`${textLink} justify-center`}
                  onClick={() => scrollToId('contact-form')}
                >
                  {t('paths.investTalk')}
                </button>
              </div>
              <p className="mt-2.5 text-[12.5px] text-mute">{t('paths.investNote')}</p>
            </div>
          </div>
          <div
            id="materials-form"
            className="mt-[18px] rounded-[20px] border border-line bg-white p-6"
          >
            <h3 className="font-ui text-[20px] font-bold leading-[1.6]">{t('form.title')}</h3>
            <p className="mt-1.5 text-[14.5px] text-ink-2">{t('form.intro')}</p>
            <InquiryForm kind="MATERIALS" />
          </div>
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-5 rounded-[22px] bg-primary-pale p-[22px] desk:px-[30px] desk:py-7">
          <div>
            <h2 className="font-ui text-[clamp(20px,2.4vw,26px)] font-bold leading-[1.6] text-ink">
              {t('contact.title')}
            </h2>
            <p className="mt-1.5 max-w-[40em] text-[#3F5243]">{t('contact.body')}</p>
          </div>
          <button
            type="button"
            className={`${btnPrimary} w-full desk:w-auto`}
            onClick={() => scrollToId('contact-form')}
          >
            {t('contact.cta')}
          </button>
        </div>
        <div id="contact-form" className="mt-[18px] rounded-[20px] border border-line bg-white p-6">
          <h3 className="font-ui text-[20px] font-bold leading-[1.6]">{t('contact.formTitle')}</h3>
          <p className="mt-1.5 text-[14.5px] text-ink-2">{t('contact.formIntro')}</p>
          <InquiryForm kind="CONTACT" />
        </div>

        <div className="mb-14 mt-10 rounded-2xl border border-dashed border-line bg-pick-bg px-5 py-[18px]">
          <h4 className="mb-2 font-ui text-[12px] font-semibold leading-[1.6] tracking-[0.08em] text-mute">
            {t('legal.title')}
          </h4>
          <p className="mb-1.5 text-[13px] leading-[1.65] text-ink-2">{t('legal.l2')}</p>
          <p className="mb-1.5 text-[13px] leading-[1.65] text-ink-2">{t('legal.l1')}</p>
          <p className="mb-1.5 text-[13px] leading-[1.65] text-ink-2">{t('legal.l3')}</p>
        </div>
      </section>
    </div>
  );
}
